import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  BarChart3, 
  Calendar,
  Clock,
  TrendingUp,
  Users,
  MapPin,
  Truck,
  Wrench,
  Loader2,
  FileText
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';
import { authHeaders } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cores para os gráficos
const COLORS = ['#FFD700', '#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316'];

export default function Productivity() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Período padrão: últimos 30 dias
  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  
  const [filters, setFilters] = useState({
    date_from: thirtyDaysAgo.toISOString().split('T')[0],
    date_to: today.toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/productivity/stats`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(filters)
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        toast.error('Erro ao carregar estatísticas');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-white font-medium">{label}</p>
          <p className="text-yellow-500 font-bold">{payload[0].value.toFixed(1)}h</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="productivity-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white flex items-center gap-3">
            <BarChart3 className="text-yellow-500" />
            Dashboard de Produtividade
          </h1>
          <p className="text-gray-400 mt-1">Análise de horas trabalhadas por máquina, operador e local</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm text-gray-400 mb-2">Período Inicial</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({...filters, date_from: e.target.value})}
              className="w-full px-4 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
              data-testid="filter-date-from"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm text-gray-400 mb-2">Período Final</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({...filters, date_to: e.target.value})}
              className="w-full px-4 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
              data-testid="filter-date-to"
            />
          </div>
          <button
            onClick={fetchStats}
            disabled={loading}
            className="px-6 py-2 bg-yellow-500 hover:bg-yellow-400 disabled:bg-gray-700 text-black font-semibold rounded-lg transition-colors flex items-center gap-2"
            data-testid="apply-filters-btn"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <TrendingUp size={18} />}
            Atualizar
          </button>
        </div>
      </div>

      {loading && !stats ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : stats ? (
        <>
          {/* Cards de Resumo */}
          <div className="grid gap-4 md:grid-cols-3">
            <div className="bg-[#111] border border-gray-800 rounded-xl p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-yellow-500/10 rounded-lg">
                  <Clock size={24} className="text-yellow-500" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Total de Horas</p>
                  <p className="text-3xl font-bold text-white">{stats.summary.total_hours}h</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[#111] border border-gray-800 rounded-xl p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-500/10 rounded-lg">
                  <FileText size={24} className="text-green-500" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">O.S. Fechadas</p>
                  <p className="text-3xl font-bold text-white">{stats.summary.total_orders}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[#111] border border-gray-800 rounded-xl p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-500/10 rounded-lg">
                  <TrendingUp size={24} className="text-blue-500" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Média por O.S.</p>
                  <p className="text-3xl font-bold text-white">{stats.summary.avg_hours_per_order}h</p>
                </div>
              </div>
            </div>
          </div>

          {/* Gráfico de Evolução no Tempo */}
          {stats.timeline.length > 0 && (
            <div className="bg-[#111] border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Calendar size={20} className="text-yellow-500" />
                Horas ao Longo do Tempo
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.timeline}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={formatDate}
                      stroke="#666"
                      tick={{ fill: '#999', fontSize: 12 }}
                    />
                    <YAxis 
                      stroke="#666"
                      tick={{ fill: '#999', fontSize: 12 }}
                      tickFormatter={(v) => `${v}h`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line 
                      type="monotone" 
                      dataKey="hours" 
                      stroke="#FFD700" 
                      strokeWidth={2}
                      dot={{ fill: '#FFD700', strokeWidth: 2 }}
                      activeDot={{ r: 6, fill: '#FFD700' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Grid de Gráficos */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Horas por Máquina */}
            {stats.by_machine.length > 0 && (
              <div className="bg-[#111] border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Truck size={20} className="text-yellow-500" />
                  Horas por Máquina
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.by_machine} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis type="number" stroke="#666" tick={{ fill: '#999', fontSize: 12 }} />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        width={120}
                        stroke="#666"
                        tick={{ fill: '#999', fontSize: 11 }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="hours" fill="#FFD700" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Horas por Operador */}
            {stats.by_operator.length > 0 && (
              <div className="bg-[#111] border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Users size={20} className="text-green-500" />
                  Horas por Operador
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.by_operator} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis type="number" stroke="#666" tick={{ fill: '#999', fontSize: 12 }} />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        width={120}
                        stroke="#666"
                        tick={{ fill: '#999', fontSize: 11 }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="hours" fill="#10B981" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Horas por Local */}
            {stats.by_location.length > 0 && (
              <div className="bg-[#111] border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <MapPin size={20} className="text-blue-500" />
                  Horas por Local
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.by_location} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis type="number" stroke="#666" tick={{ fill: '#999', fontSize: 12 }} />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        width={120}
                        stroke="#666"
                        tick={{ fill: '#999', fontSize: 11 }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="hours" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Horas por Tipo de Operação */}
            {stats.by_operation_type.length > 0 && (
              <div className="bg-[#111] border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Wrench size={20} className="text-purple-500" />
                  Horas por Tipo de Operação
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.by_operation_type}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="hours"
                        nameKey="name"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={false}
                      >
                        {stats.by_operation_type.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => [`${value}h`, 'Horas']}
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                        labelStyle={{ color: '#fff' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {/* Legenda */}
                <div className="flex flex-wrap gap-2 mt-4 justify-center">
                  {stats.by_operation_type.map((item, index) => (
                    <div key={item.name} className="flex items-center gap-1 text-xs">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-gray-400">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Mensagem se não houver dados */}
          {stats.summary.total_orders === 0 && (
            <div className="bg-[#111] border border-gray-800 rounded-xl p-12 text-center">
              <BarChart3 size={48} className="text-gray-600 mx-auto mb-4" />
              <h3 className="text-white text-lg font-semibold mb-2">Nenhum dado no período</h3>
              <p className="text-gray-500">
                Não há ordens de serviço fechadas no período selecionado.
              </p>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
